import { cloneDeep } from "lodash-es";
import React, { FC, ReactElement, useState } from "react";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";

import { CircleMinus, CirclePlus } from "../../../../components/icons";
import TextField from "../../../../components/styled/TextField";
import {
  getRouteFormProperty,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../store";
import { IntegerRange } from "../../../../store/pomeriumApi";
import {
  validateExpectedEndStatus,
  validateExpectedStartStatus,
} from "../../../../validators/expectedStatuses";

type Props = {
  readonly: boolean;
};

export const ExpectedStatusList: FC<Props> = ({ readonly }): ReactElement => {
  const statusList = useSelector(
    getRouteFormProperty("healthCheck.expectedStatuses", []),
  ) as IntegerRange[];
  const [localValues, setLocalValues] = useState<IntegerRange[]>(
    statusList || [],
  );
  const dispatch = useDispatch();

  const save = (arr: IntegerRange[]) => {
    dispatch(
      setRouteFormProperty({
        path: "healthCheck.expectedStatuses",
        value: arr,
      }),
    );
  };

  const handleAdd = (): void => {
    const nvs = cloneDeep(localValues);
    nvs.push({ start: 200, end: 200 });
    setLocalValues(nvs);
  };

  const handleChange = (
    idx: number,
    position: "start" | "end",
    value: number,
  ): void => {
    const nvs = cloneDeep(localValues);
    nvs[idx][position] = value;
    setLocalValues(nvs);
  };

  const handleDelete = (idx: number): void => {
    const nvs = cloneDeep(localValues);
    nvs.splice(idx, 1);
    setLocalValues(nvs);
    save(nvs);
  };

  const handleBlur = (
    idx: number,
    position: "start" | "end",
    value: number,
  ): void => {
    const nvs = cloneDeep(localValues);
    nvs[idx][position] = value;
    save(nvs);
  };

  return (
    <Grid container>
      <Grid
        key={"expected-status-header"}
        container
        spacing={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Grid size={5}>
          <InputLabel sx={{ textAlign: "left", mb: 1, pl: 1 }}>
            Range Start
          </InputLabel>
        </Grid>
        <Grid size={5}>
          <InputLabel sx={{ textAlign: "left", mb: 1, pl: 1 }}>
            Range End
          </InputLabel>
        </Grid>
        <Grid size={2}>
          <IconButton size="small" onClick={(): void => handleAdd()}>
            <CirclePlus />
          </IconButton>
        </Grid>
      </Grid>
      {localValues?.map((rangeItem, i) => {
        const helperTextStart = validateExpectedStartStatus(
          rangeItem.start,
          rangeItem.end,
        );

        const helperTextEnd = validateExpectedEndStatus(
          rangeItem.start,
          rangeItem.end,
        );

        return (
          <Grid
            key={i}
            container
            spacing={1}
            alignItems="center"
            alignContent="center"
          >
            <Grid sx={{ minHeight: "100px" }} size={5}>
              <TextField
                disabled={readonly}
                fullWidth
                value={rangeItem.start}
                error={!!helperTextStart}
                helperText={helperTextStart}
                onChange={(
                  evt: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >,
                ): void => {
                  handleChange(
                    i,
                    "start",
                    Number(parseInt(evt.target.value)) || 0,
                  );
                }}
                onBlur={(
                  evt: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >,
                ): void => {
                  handleBlur(
                    i,
                    "start",
                    Number(parseInt(evt.target.value)) || 0,
                  );
                }}
              />
            </Grid>
            <Grid sx={{ minHeight: "100px" }} size={5}>
              <TextField
                disabled={readonly}
                fullWidth
                value={rangeItem.end}
                error={!!helperTextEnd}
                helperText={helperTextEnd}
                onChange={(
                  evt: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >,
                ): void => {
                  handleChange(
                    i,
                    "end",
                    Number(parseInt(evt.target.value)) || 0,
                  );
                }}
                onBlur={(
                  evt: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >,
                ): void => {
                  handleBlur(i, "end", Number(parseInt(evt.target.value)) || 0);
                }}
              />
            </Grid>
            {!readonly && (
              <Grid sx={{ minHeight: "100px" }} size={2}>
                <IconButton size="small" onClick={(): void => handleDelete(i)}>
                  <CircleMinus />
                </IconButton>{" "}
              </Grid>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};
